import { __assign, __extends, __rest } from "tslib";
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Slide from 'rc-slider';
import { dataHooks } from './constants';
import { generateID } from '../utils/generateId';
import SliderHandle from './SliderHandle';
import Text from '../Text';
import { st, classes } from './Slider.st.css';
var range = function (_a) {
    var min = _a.min, max = _a.max, step = _a.step;
    var arr = [];
    for (var i = min; i <= max; i += step) {
        arr.push(i);
    }
    return arr;
};
/**
 * A slider component with multi-range support
 */
var Slider = /** @class */ (function (_super) {
    __extends(Slider, _super);
    function Slider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._renderHandle = function (handleProps) {
            var _a = _this.props, displayTooltip = _a.displayTooltip, disabled = _a.disabled, marks = _a.marks, dataHook = _a.dataHook, direction = _a.direction, onFocus = _a.onFocus, onBlur = _a.onBlur;
            var index = handleProps.index, value = handleProps.value, min = handleProps.min, max = handleProps.max, offset = handleProps.offset, ref = handleProps.ref, ariaLabel = handleProps.ariaLabel, reverse = handleProps.reverse;
            var tooltipValue;
            if (_this._isCustomMarks()) {
                if (marks.hasOwnProperty(value)) {
                    tooltipValue = marks[value].toString();
                }
            }
            else {
                tooltipValue = value.toString();
            }
            return (React.createElement(SliderHandle, { key: index, disabled: disabled, displayTooltip: displayTooltip, tooltipValue: tooltipValue, handleProps: { ref: ref, offset: offset, min: min, max: max, value: value, reverse: reverse, ariaLabel: ariaLabel }, index: index, dataHook: dataHook, direction: direction, onBlurCustom: onBlur, onFocusCustom: onFocus }));
        };
        _this._renderSlider = function () {
            var _a = _this.props, onFocus = _a.onFocus, onBlur = _a.onBlur, pushable = _a.pushable, allowCross = _a.allowCross, value = _a.value, displayMarks = _a.displayMarks, direction = _a.direction, startPoint = _a.startPoint, dataHook = _a.dataHook, id = _a.id, ariaLabelForHandle = _a.ariaLabelForHandle, rtl = _a.rtl, className = _a.className, rest = __rest(_a, ["onFocus", "onBlur", "pushable", "allowCross", "value", "displayMarks", "direction", "startPoint", "dataHook", "id", "ariaLabelForHandle", "rtl", "className"]);
            return Array.isArray(value) && value.length > 1 ? (React.createElement(Slide.Range, __assign({}, rest, { vertical: direction === 'vertical', prefixCls: "wsr-slider", handle: _this._renderHandle, marks: displayMarks ? _this._getMarks() : {}, value: value, pushable: pushable, allowCros: allowCross, reverse: rtl, ariaLabelGroupForHandles: ariaLabelForHandle }))) : (React.createElement(Slide, __assign({}, rest, { vertical: direction === 'vertical', prefixCls: "wsr-slider", startPoint: startPoint, handle: _this._renderHandle, marks: displayMarks ? _this._getMarks() : {}, value: Array.isArray(value) ? value[0] : value, reverse: rtl, ariaLabelForHandle: Array.isArray(ariaLabelForHandle)
                    ? ariaLabelForHandle[0]
                    : ariaLabelForHandle })));
        };
        return _this;
    }
    Slider.prototype._getMarks = function () {
        var _this = this;
        var marksLabels = {};
        if (this._isCustomMarks()) {
            var marks = this.props.marks;
            Object.entries(marks).forEach(function (_a) {
                var key = _a[0], value = _a[1];
                marksLabels[key] = {
                    label: _this._createMarkNode(value, true),
                };
            });
        }
        else {
            var _a = this.props, min_1 = _a.min, max_1 = _a.max, step = _a.step, startPoint_1 = _a.startPoint;
            range({ min: min_1, max: max_1, step: step }).forEach(function (entry) {
                var shouldRenderMarkLabel = entry === min_1 || entry === max_1 || entry === startPoint_1;
                marksLabels[entry] = {
                    label: _this._createMarkNode(entry, shouldRenderMarkLabel),
                };
            });
        }
        return marksLabels;
    };
    Slider.prototype._isCustomMarks = function () {
        var marks = this.props.marks;
        return Object.entries(marks).length > 0;
    };
    Slider.prototype._createMarkNode = function (value, shouldRenderMarkLabel) {
        return (React.createElement("div", { className: st(classes.mark, { direction: this.props.direction }) },
            React.createElement("div", { className: classes.markLine }),
            React.createElement("div", { className: classes.markValue }, shouldRenderMarkLabel && (React.createElement(Text, { className: classes.markLabel, dataHook: dataHooks.sliderMarkLabel, weight: "thin", size: "small" }, value)))));
    };
    Slider.prototype.render = function () {
        var _a = this.props, dataHook = _a.dataHook, id = _a.id, direction = _a.direction, className = _a.className;
        return (React.createElement("div", { className: st(classes.root, className), id: id, "data-hook": dataHook, "data-direction": direction }, this._renderSlider()));
    };
    return Slider;
}(Component));
export default Slider;
Slider.displayName = 'Slider';
Slider.propTypes = {
    /** Allows sliders handles to cross. */
    allowCross: PropTypes.bool,
    /** Applies a data-hook HTML attribute that can be used in the tests. */
    dataHook: PropTypes.string,
    /** Specifies a CSS class name to be appended to the component’s root element. */
    className: PropTypes.string,
    /** Controls the visibility of the marks. */
    displayMarks: PropTypes.bool,
    /** Controls visibility of slide handle tooltip */
    displayTooltip: PropTypes.bool,
    /** Assigns an unique identifier for the root element. */
    id: PropTypes.string,
    /** Sets the absolute maximum value of sliders range. */
    max: PropTypes.number,
    /** Sets the absolute minimum value of the sliders range. */
    min: PropTypes.number,
    /** Specifies slider marks. The key determines the position, and the value determines what will show. The object structure should be either
     * ```{ number : number}``` / ```{ number : string }```
     * */
    marks: PropTypes.object,
    /** Defines a callback function which will be called when ontouchend or onmouseup is triggered. */
    onAfterChange: PropTypes.func,
    /** Defines a callback function which will be called when ontouchstart or onmousedown is triggered. */
    onBeforeChange: PropTypes.func,
    /** Defines a callback function which is called upon every value change. */
    onChange: PropTypes.func.isRequired,
    /** Adjust component for RTL. */
    rtl: PropTypes.bool,
    /** Specifies the interval between range values. */
    step: PropTypes.number,
    /** Push surrounding handles when moving a handle (relevant for multi value sliders only). Number specifies a minimum distance between handles. */
    pushable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    /** Specifies the selected value. */
    value: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.number),
        PropTypes.number,
    ]),
    /** Specifies whether interactions are disabled.  */
    disabled: PropTypes.bool,
    /** Specifies the starting value of a track. If undefined, the minimum value of a range is used as a starting point. */
    startPoint: PropTypes.number,
    /** Sets slider direction in either horizontal way or vertical */
    direction: PropTypes.oneOf(['vertical', 'horizontal']),
    /** Set the aria-label attributes for slider handles. */
    ariaLabelForHandle: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
    ]),
    /** Sets the onFocus callbak for the slider's handle.
     * ```javascript
     *  onFocus((handleValue) => ({}))
     * ```
     */
    onFocus: PropTypes.func,
    /** Sets the onBlur callbak for the slider's handle.
     * ```javascript
     *  onBlur((handleValue) => ({}))
     * ```
     */
    onBlur: PropTypes.func,
};
Slider.defaultProps = {
    min: 1,
    max: 20,
    step: 1,
    value: 3,
    allowCross: true,
    id: generateID(),
    displayTooltip: true,
    displayMarks: true,
    marks: {},
    rtl: false,
    disabled: false,
    startPoint: undefined,
};
//# sourceMappingURL=Slider.js.map